/**
 * <header-component>
 *
 * Web Component that owns all header behavior:
 *  - keeps CSS variables in sync via ResizeObserver (no resize-listener + setTimeout)
 *  - detects sticky state via IntersectionObserver (no scroll polling)
 *  - rAF-throttled scroll handler (registered via shared scrollListener) for
 *    scroll-driven classes and transparent toggle
 *  - menu open/close on hover + focus, body scroll lock, aria-expanded management
 *  - responsive menu overlap logic (menu drops to its own row when links wrap)
 *  - dispatches `menu:open` / listens for `menu:close`
 *
 * Loaded ONLY from `sections/main-header.liquid`. Does not live in global.js.
 *
 * External dependencies:
 *   - @theme/body-scroll-lock + @theme/body-scroll-lock-options
 *   - @theme/scroll-listener (shared window scroll multiplexer)
 *   - @theme/responsive (breakpoint checks — desktop menu only runs at lap_and_up)
 *
 * Public contract (do not break — CSS and other scripts depend on these):
 *   CSS vars set on documentElement:
 *     --header-height, --header-height-fixed, --submenu-position, --header-dynamic-height
 *   CSS classes:
 *     on this element: Header--Scrolled, Header--Transparent
 *     on parent:       header--Sticky
 *   Events:
 *     dispatches: menu:open
 *     listens:    menu:close (on body), cart:updated + product:added (on document)
 *   Attributes:
 *     [data-has-menu] items toggle aria-expanded on their trigger link; [data-submenu] panels close on mouseleave
 *   Keyboard: submenus open on focusin, close on focusout of the <li> (Tab past the last link)
 *             or on Escape (focus returns to the trigger link). Disclosure-navigation pattern.
 */

import { bodyScrollLockOptions } from '@theme/body-scroll-lock-options';
import { disableBodyScroll, enableBodyScroll } from '@theme/body-scroll-lock';
import { syncHeaderCartCount } from '@theme/cart-visible-count';
import { scrollListener } from '@theme/scroll-listener';
import { responsive } from '@theme/responsive';

class HeaderComponent extends HTMLElement {
  #options = { transparent: false, sticky: false };
  #activeScrollTarget = null;

  #resizeObserver = null;
  #stickyObserver = null;
  #scrollRafId = null;
  #menuOverlapRafId = null;
  #scrollHandle = null;
  #resizeHandle = null;
  #initialized = false;

  #menuTriggers = [];
  #singleMenuTargets = [];
  #focusOutTargets = [];
  #savedOverlapWidth = 0;
  #dropdownHeadings = [];
  #cartUpdatedHandler = null;

  #onCartSync = () => {
    syncHeaderCartCount();
  };

  #onResize = () => {
    if (this.#menuOverlapRafId !== null) return;
    this.#menuOverlapRafId = requestAnimationFrame(() => {
      this.#menuOverlapRafId = null;
      this.#menuOverlapHandler();
    });
  };

  #onSectionLoad = () => {
    this.#attachMenuListeners();
    this.#menuOverlapHandler();
  };

  #onScroll = () => {
    if (this.#scrollRafId !== null) return;
    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.#options = this.#parseOptions();

    this.#setupHeightObserver();

    // Sticky is opt-in via the section's `sticky_header` setting (sent through
    // data-settings as `sticky`). Previously `header--Sticky` was added
    // unconditionally, so the header stayed sticky even when the merchant turned
    // the setting off. Only stick — the class (position:sticky) and its
    // stick-detection observer — when the setting is on.
    if (this.#options.sticky) {
      this.parentElement?.classList.add('header--Sticky');
      this.#setupStickyObserver();
    }

    this.#scrollHandle = scrollListener.addScrollListener(this, this.#onScroll);

    // delegate-exempt: custom event bus on document.body, no DOM target to delegate from
    document.body.addEventListener('menu:close', this.#closeMenu);
    this.#attachMenuListeners();
    // Keyboard: Escape closes the open submenu and returns focus to its trigger.
    this.addEventListener('keydown', this.#onKeydown);

    this.#resizeHandle = responsive.addResizeListener(this, this.#onResize);
    // delegate-exempt: Shopify theme editor lifecycle event fires on document
    document.addEventListener('shopify:section:load', this.#onSectionLoad);

    this.#cartUpdatedHandler = this.#onCartSync;
    document.addEventListener('cart:updated', this.#cartUpdatedHandler);
    document.addEventListener('product:added', this.#cartUpdatedHandler);

    this.#menuOverlapHandler();
    this.#updateScrollState();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#stickyObserver?.disconnect();
    // Clean up the one class this component owns on the parent, so it doesn't
    // leak across Theme Editor section re-renders (re-added on connect if sticky).
    this.parentElement?.classList.remove('header--Sticky');

    if (this.#scrollRafId !== null) cancelAnimationFrame(this.#scrollRafId);
    if (this.#menuOverlapRafId !== null) cancelAnimationFrame(this.#menuOverlapRafId);

    this.#scrollHandle?.removeScrollListener();
    this.#scrollHandle = null;
    this.#resizeHandle?.removeResizeListener();
    this.#resizeHandle = null;
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
    if (this.#cartUpdatedHandler) {
      document.removeEventListener('cart:updated', this.#cartUpdatedHandler);
      document.removeEventListener('product:added', this.#cartUpdatedHandler);
      this.#cartUpdatedHandler = null;
    }
    document.body.removeEventListener('menu:close', this.#closeMenu);
    this.removeEventListener('keydown', this.#onKeydown);

    this.#detachMenuListeners();

    // Release any active body-scroll lock so it doesn't leak across Theme Editor
    // section re-renders.
    if (this.#activeScrollTarget) {
      enableBodyScroll(this.#activeScrollTarget);
      this.#activeScrollTarget = null;
    }

    this.#initialized = false;

    document.documentElement.style.removeProperty('--header-height');
    document.documentElement.style.removeProperty('--header-height-fixed');
    document.documentElement.style.removeProperty('--submenu-position');
    document.documentElement.style.removeProperty('--header-dynamic-height');
  }

  #parseOptions() {
    try {
      return JSON.parse(this.getAttribute('data-settings')) ?? {};
    } catch {
      return {};
    }
  }


  #setupHeightObserver() {
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry?.borderBoxSize?.[0]) return;
      const height = Math.round(entry.borderBoxSize[0].blockSize);
      const px = `${height}px`;
      const root = document.documentElement.style;
      root.setProperty('--header-height', px);
      root.setProperty('--header-height-fixed', px);
      root.setProperty('--submenu-position', px);
    });
    this.#resizeObserver.observe(this);
  }

  #setupStickyObserver() {
    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.dataset.stickyState = entry.isIntersecting ? 'inactive' : 'active';
      },
      { threshold: 1 }
    );
    this.#stickyObserver.observe(this);
  }

  #attachMenuListeners() {
    this.#detachMenuListeners();

    this.#menuTriggers = Array.from(this.querySelectorAll('[data-has-menu]'));
    this.#menuTriggers.forEach((el) => {
      el.addEventListener('mouseenter', this.#openMenu);
      el.addEventListener('focusin', this.#openMenu);
      el.addEventListener('focusout', this.#onMenuFocusOut);
    });

    // Close when the pointer leaves the whole header. For hover purposes the
    // header's region includes the open submenu panel (a descendant of the
    // [data-has-menu] <li>), so moving the cursor from a nav link DOWN into the
    // panel — across the header's bottom padding — stays "inside" and never
    // crosses a dead zone that closes the menu. (Was bound per [data-submenu]
    // panel, which left that gap.) A no-submenu sibling still closes it via the
    // [data-single-menu] mouseenter handler below.
    this.addEventListener('mouseleave', this.#closeMenu);

    this.#singleMenuTargets = Array.from(this.querySelectorAll('[data-single-menu]'));
    this.#singleMenuTargets.forEach((el) => {
      el.addEventListener('mouseenter', this.#closeMenu);
    });

    // Focusing a no-submenu top-level item closes any open submenu.
    // NOTE: do NOT include `.header-main-links` here — that races the focusin-open
    // on the trigger and slams the submenu shut for keyboard users.
    this.#focusOutTargets = Array.from(this.querySelectorAll('[data-single-menu]'));
    this.#focusOutTargets.forEach((el) => {
      el.addEventListener('focusin', this.#closeMenu);
    });

    // Whole heading row is the trigger (not just the arrow) for a larger hit area.
    this.#dropdownHeadings = Array.from(
      this.querySelectorAll('.desktop-menu__dropdown-heading')
    );
    this.#dropdownHeadings.forEach((el) => {
      el.addEventListener('click', this.#toggleDropdown);
    });
  }

  #detachMenuListeners() {
    this.#menuTriggers.forEach((el) => {
      el.removeEventListener('mouseenter', this.#openMenu);
      el.removeEventListener('focusin', this.#openMenu);
      el.removeEventListener('focusout', this.#onMenuFocusOut);
    });
    this.removeEventListener('mouseleave', this.#closeMenu);
    this.#singleMenuTargets.forEach((el) => {
      el.removeEventListener('mouseenter', this.#closeMenu);
    });
    this.#focusOutTargets.forEach((el) => {
      el.removeEventListener('focusin', this.#closeMenu);
    });
    this.#dropdownHeadings.forEach((el) => {
      el.removeEventListener('click', this.#toggleDropdown);
    });
    this.#menuTriggers = [];
    this.#singleMenuTargets = [];
    this.#focusOutTargets = [];
    this.#dropdownHeadings = [];
  }

  #toggleDropdown = (event) => {
    // The heading is itself the disclosure <button> (type="button", no nav), so it
    // is the control — no preventDefault needed.
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;

    const content = button
      .closest('.desktop-menu__dropdown')
      ?.querySelector('.desktop-menu__dropdown-content');
    if (!content) return;

    const willOpen = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(willOpen));
    content.setAttribute('aria-hidden', String(!willOpen));
  };

  #openMenu = (event) => {
    const item = event.currentTarget;
    if (!(item instanceof HTMLElement)) return;

    this.dispatchEvent(new CustomEvent('menu:open', { bubbles: true }));

    this.#updateMenuPosition();

    // NB: do NOT remove Header--Transparent here. Keeping it (and solidifying via
    // the `:has(.header-main-links[aria-expanded='true'])` CSS rule) prevents the
    // .Header--Bg border-bottom from transitioning in — that was the line that
    // flashed across the header bottom on hover.

    // aria-expanded lives on the trigger link (the disclosure control), not the <li>.
    item.querySelector('.header-main-links')?.setAttribute('aria-expanded', 'true');

    // Only one top-level submenu open at a time — collapse the others.
    this.#menuTriggers.forEach((other) => {
      if (other === item) return;
      other.querySelector('.header-main-links')?.setAttribute('aria-expanded', 'false');
    });

    // Lock body scroll against THIS menu's panel — each submenu wrapper is its own
    // [data-scrollable], so the target must match the open panel (not just the
    // first one). Release the previously-locked panel first when switching menus.
    const scrollTarget = item.querySelector('[data-scrollable]');
    if (scrollTarget !== this.#activeScrollTarget) {
      if (this.#activeScrollTarget) enableBodyScroll(this.#activeScrollTarget);
      this.#activeScrollTarget = scrollTarget;
      if (scrollTarget) disableBodyScroll(scrollTarget, bodyScrollLockOptions);
    }
  };

  #closeMenu = () => {
    this.querySelectorAll('[aria-expanded="true"]').forEach((item) => {
      item.setAttribute('aria-expanded', 'false');
    });
    this.querySelectorAll('.desktop-menu__dropdown-content').forEach((content) => {
      content.setAttribute('aria-hidden', 'true');
    });

    this.#updateScrollState();

    if (this.#activeScrollTarget) {
      enableBodyScroll(this.#activeScrollTarget);
      this.#activeScrollTarget = null;
    }
  };

  // Close the submenu once focus leaves the whole <li> (tabbing past the last
  // submenu link). Moving between the trigger and its own submenu links keeps it open.
  #onMenuFocusOut = (event) => {
    const item = event.currentTarget;
    if (!(item instanceof HTMLElement)) return;
    if (item.contains(event.relatedTarget)) return;
    this.#closeMenu();
  };

  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const trigger = this.querySelector('.header-main-links[aria-expanded="true"]');
    if (!trigger) return;
    this.#closeMenu();
    trigger.focus();
  };

  #updateMenuPosition() {
    document.documentElement.style.setProperty('--submenu-position', `${this.offsetHeight}px`);
  }

  #updateScrollState() {
    const scrollY = window.scrollY;

    if (scrollY > 25) {
      this.classList.add('Header--Scrolled');
    } else {
      this.classList.remove('Header--Scrolled');
    }

    if (this.#options.transparent) {
      if (scrollY > 25) {
        this.classList.remove('Header--Transparent');
      } else {
        this.classList.add('Header--Transparent');
      }
    }

    const rect = this.getBoundingClientRect();
    const visibleHeight = Math.max(
      0,
      Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)
    );
    document.documentElement.style.setProperty(
      '--header-dynamic-height',
      `${Math.round(visibleHeight)}px`
    );
  }

  #menuOverlapHandler() {
    // Desktop menu is hidden below 1025px (not_mobile not_pocket); skip all the
    // measure/mutate work on pocket/phone viewports.
    if (!responsive.matchesBreakpoint('lap_and_up')) return;

    const menuItems = this.querySelector('.header-menu-items');
    const menuWrapper = this.querySelector('.header--Wrapper');
    if (!menuItems || !menuWrapper) return;

    // Batch reads — every offsetHeight read forces a synchronous layout, so group
    // them (and the width read) before touching the DOM.
    const heights = [];
    this.querySelectorAll('.header-main-links').forEach((item) => heights.push(item.offsetHeight));
    const maxHeight = heights.length ? Math.max.apply(null, heights) : 0;
    const minHeight = heights.length ? Math.min.apply(null, heights) : 0;
    const overlapping = maxHeight > minHeight;
    const width = document.documentElement.clientWidth;

    // Then batch writes. The overlap breakpoint width is component state — keep it
    // on a #private field, not window.theme (which is read-only config from theme.liquid).
    if (overlapping && !this.#savedOverlapWidth) {
      this.#savedOverlapWidth = width;
    }

    if (this.#savedOverlapWidth && width > this.#savedOverlapWidth) {
      menuItems.classList.remove('mt-m', 'mainMenu--Overlapping');
      menuWrapper.querySelector('.header-main-menu-items')?.prepend(menuItems);
      this.#savedOverlapWidth = 0;
    } else if (overlapping) {
      menuItems.classList.add('mt-m', 'mainMenu--Overlapping');
      menuItems.closest('.header')?.appendChild(menuItems);
    }

    // Reveal the menu once layout has been measured — the <ul> ships with
    // inline opacity:0 (FOUC guard) and only this handler is allowed to undo it.
    menuItems.style.opacity = 1;
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}
